import { readRefundSupervisorThreshold } from 'utils/refunds/supervisorApproval';
import type { RefundSupervisorThreshold } from 'utils/refunds/supervisorApproval';

import { patientsApi } from '../patientApi';
import {
  CalculateRefundRecommendationRequest,
  CalculateRefundRecommendationResponse,
  CancelInvoiceRequestProps,
  CancelInvoiceResponseProps,
  EnhancedRefundInvoiceRequestProps,
  GetRefundRecommendationHistoryResponse,
  RefundExecutionResponse,
  RefundInvoiceRequestProps,
  RefundInvoiceResponseProps,
  RefundOperationStatusRequest,
  RefundRecommendationHistoryRequest,
  RetryInvoicePaymentRequestProps
} from './invoices.types';
import {
  parseCalculateRefundRecommendationResponse,
  parseRefundExecutionResponse,
  parseRefundRecommendationHistoryResponse
} from './refundWire';

const refundParsingError = (_data: unknown, originalStatus: number) => ({
  error: {
    data: 'Refund operation response failed runtime validation',
    error: 'Invalid refund operation wire response',
    originalStatus,
    status: 'PARSING_ERROR' as const
  }
});

const resemblesExecutionEnvelope = (data: unknown): boolean =>
  typeof data === 'object' &&
  data !== null &&
  ('executionCode' in data || 'executionStatus' in data || 'operationId' in data);

const getHttpStatus = (result: {
  error?: { status: number | string };
  meta?: unknown;
}): number | undefined => {
  if (typeof result.error?.status === 'number') return result.error.status;
  if (typeof result.meta !== 'object' || result.meta === null || !('response' in result.meta))
    return undefined;
  const response = result.meta.response;
  return response instanceof Response ? response.status : undefined;
};

export const invoicesApi = patientsApi.injectEndpoints({
  endpoints: (builder) => ({
    calculateRefundRecommendation: builder.mutation<
      CalculateRefundRecommendationResponse,
      CalculateRefundRecommendationRequest
    >({
      async queryFn(
        { cancellationEffectiveDate, invoiceUid, patientId },
        _api,
        _extraOptions,
        baseQuery
      ) {
        const result = await baseQuery({
          body: { cancellationEffectiveDate, patientId: patientId.trim() },
          method: 'POST',
          url: `/invoices/${invoiceUid}/refund-recommendation`
        });
        if (result.error) return { error: result.error };
        try {
          return { data: parseCalculateRefundRecommendationResponse(result.data) };
        } catch {
          return refundParsingError(result.data, 200);
        }
      }
    }),
    cancelInvoice: builder.mutation<CancelInvoiceResponseProps, CancelInvoiceRequestProps>({
      invalidatesTags: ['Billing', { id: 'LIST', type: 'Tags' }],
      query: ({ body, id }) => ({
        body,
        method: 'POST',
        url: `/invoices/${id}/cancel`
      })
    }),
    getRefundOperationStatus: builder.mutation<
      RefundExecutionResponse,
      RefundOperationStatusRequest
    >({
      async queryFn({ invoiceUid, operationId, patientId }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          body: { patientId },
          method: 'POST',
          url: `/invoices/${invoiceUid}/refund-operations/${operationId}/status`
        });
        const status = getHttpStatus(result);
        const data = result.error?.data ?? result.data;
        if (typeof status !== 'number')
          return result.error ? { error: result.error } : refundParsingError(data, 0);
        try {
          return {
            data: parseRefundExecutionResponse(data, status, { invoiceUid, operationId })
          };
        } catch {
          return result.error && !resemblesExecutionEnvelope(data)
            ? { error: result.error }
            : refundParsingError(data, status);
        }
      }
    }),
    getRefundRecommendationHistory: builder.query<
      GetRefundRecommendationHistoryResponse,
      RefundRecommendationHistoryRequest
    >({
      providesTags: (_result, _error, { invoiceUid }) => [
        { id: invoiceUid, type: 'RefundRecommendationHistory' }
      ],
      async queryFn({ cursor, invoiceUid, limit }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          params: {
            ...(cursor === undefined ? {} : { cursor }),
            ...(limit === undefined ? {} : { limit })
          },
          url: `/invoices/${invoiceUid}/refund-recommendations`
        });
        if (result.error) return { error: result.error };
        try {
          return { data: parseRefundRecommendationHistoryResponse(result.data, invoiceUid) };
        } catch {
          return refundParsingError(result.data, 200);
        }
      }
    }),
    getRefundSupervisorApprovalThreshold: builder.query<RefundSupervisorThreshold, void>({
      query: () => ({ url: '/refund-supervisor-approval-threshold' }),
      transformResponse: readRefundSupervisorThreshold
    }),
    refundInvoice: builder.mutation<RefundInvoiceResponseProps, RefundInvoiceRequestProps>({
      invalidatesTags: (_result, _error, { id }) => [
        'Billing',
        { id: 'LIST', type: 'Tags' },
        { id, type: 'RefundRecommendationHistory' }
      ],
      query: ({ body, id }) => ({
        body,
        method: 'POST',
        url: `/invoices/${id}/refunds`
      })
    }),
    retryInvoicePayment: builder.mutation<{ message: string }, RetryInvoicePaymentRequestProps>({
      invalidatesTags: ['Billing'],
      query: ({ invoiceId, patientId }) => ({
        method: 'POST',
        url: `/invoices/${patientId}/${invoiceId}/retry-payment`
      })
    }),
    submitRefundOperation: builder.mutation<
      RefundExecutionResponse,
      EnhancedRefundInvoiceRequestProps
    >({
      async queryFn({ body, id }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({ body, method: 'POST', url: `/invoices/${id}/refunds` });
        const status = getHttpStatus(result);
        const data = result.error?.data ?? result.data;
        if (typeof status !== 'number')
          return result.error ? { error: result.error } : refundParsingError(data, 0);
        try {
          return {
            data: parseRefundExecutionResponse(data, status, {
              invoiceUid: id,
              operationId: body.operationId
            })
          };
        } catch {
          return result.error && !resemblesExecutionEnvelope(data)
            ? { error: result.error }
            : refundParsingError(data, status);
        }
      }
    })
  })
});

export const {
  useCalculateRefundRecommendationMutation,
  useCancelInvoiceMutation,
  useGetRefundOperationStatusMutation,
  useGetRefundRecommendationHistoryQuery,
  useLazyGetRefundRecommendationHistoryQuery,
  useLazyGetRefundSupervisorApprovalThresholdQuery,
  useRefundInvoiceMutation,
  useRetryInvoicePaymentMutation,
  useSubmitRefundOperationMutation
} = invoicesApi;

export {
  parseCalculateRefundRecommendationResponse,
  parseRefundExecutionResponse,
  parseRefundRecommendationHistoryResponse
};
